import { useEffect, useRef, useState } from "react";
import { useCreateAgent } from "./useAgentsUi";

/**
 * "New agent" dialog. Asks the user, in plain language, what they want the agent
 * for; on submit it scaffolds a complete, editable agent (via useCreateAgent) and
 * hands the new id back so the caller can open it straight into the Build lens.
 *
 * Escape or an overlay click cancels; ⌘/Ctrl+Enter submits.
 */
export function NewAgentDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (agentId: string) => void;
}) {
  const [purpose, setPurpose] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const create = useCreateAgent();

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = () => {
    const text = purpose.trim();
    if (!text || create.isPending) return;
    create.mutate(text, { onSuccess: (agent) => onCreated(agent.id) });
  };

  // Grow the textarea to fit its content instead of letting the user drag-resize it.
  const autoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  return (
    <div className="agui-modal__overlay" onMouseDown={onClose}>
      <div
        className="agui-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="agui-newagent-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 id="agui-newagent-title" className="agui-modal__title">
          What do you want this agent for?
        </h2>
        <p className="agui-modal__sub">
          Describe the job in plain language — we’ll scaffold an agent you can
          fine-tune in Build.
        </p>
        <textarea
          ref={inputRef}
          className="agui-modal__input xv-scroll"
          value={purpose}
          rows={4}
          aria-label="What do you want this agent for?"
          placeholder="e.g. Draft replies to investors who ask to meet, and propose 3 calendar slots."
          onChange={(e) => {
            setPurpose(e.target.value);
            autoGrow(e.currentTarget);
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
        />
        <div className="agui-modal__actions">
          <button type="button" className="agui-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="agui-btn agui-btn--primary"
            disabled={!purpose.trim() || create.isPending}
            onClick={submit}
          >
            {create.isPending ? "Creating…" : "Create agent"}
          </button>
        </div>
      </div>
    </div>
  );
}
